/**
 * API utilities and common patterns for data fetching
 */

import { logger } from './logger';

// No-cache headers to prevent caching
export const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
  'Expires': '0'
};

// Common error handler for API calls
export const handleApiError = (error: any, context: string): string => {
  logger.error(`Error in ${context}`, 'APIHelpers', error);
  
  if (error.response?.status === 404) {
    return 'داده‌ای یافت نشد';
  } else if (error.response?.status === 500) {
    return 'خطای سرور داخلی';
  } else if (error.code === 'NETWORK_ERROR') {
    return 'خطا در اتصال به سرور';
  } else {
    return `خطا در ${context}`;
  }
};

// Common loading state management
export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

export const createLoadingState = (): LoadingState => ({
  isLoading: false,
  error: null
});

export const setLoadingState = (
  setState: React.Dispatch<React.SetStateAction<LoadingState>>,
  isLoading: boolean,
  error: string | null = null
) => {
  setState({ isLoading, error });
};

// Retry mechanism for failed API calls
export const retryApiCall = async <T>(
  apiCall: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  
  throw lastError;
};

// Common fetch function with error handling
export const fetchWithErrorHandling = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...NO_CACHE_HEADERS,
        ...options.headers
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json() as T;
    return data;
  } catch (error) {
    logger.error('Error in fetchWithErrorHandling', 'APIHelpers', error);
    throw error;
  }
};

// Type for API response with success field
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Helper to validate API response
export const validateApiResponse = <T>(response: any): response is ApiResponse<T> => {
  return response && typeof response.success === 'boolean';
};